import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Mail, Webhook, ShieldCheck, Plus, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "org_admin";
  const [wa, setWa] = useState(null);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer", supplier_id: "" });

  const load = () => {
    if (isAdmin) {
      api.get("/integrations/whatsapp").then((r) => setWa(r.data)).catch(() => {});
      api.get("/users").then((r) => setUsers(r.data.users)).catch(() => {});
      api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    }
    api.get("/suppliers").then((r) => setSuppliers(r.data.suppliers)).catch(() => {});
  };
  useEffect(() => { load(); }, [isAdmin]);

  const saveWa = async () => {
    setSaving(true);
    try {
      await api.put("/integrations/whatsapp", wa);
      toast.success("WhatsApp configuration saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { ...form, supplier_id: form.role === "supplier" ? form.supplier_id : null });
      toast.success("User created");
      setAddOpen(false);
      setForm({ name: "", email: "", password: "", role: "buyer", supplier_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-2xl rounded-lg border bg-card p-8" data-testid="settings-readonly">
        <h1 className="font-heading text-xl font-semibold">Settings</h1>
        <p className="mt-2 text-sm text-muted-foreground">Organization settings and integrations are managed by your organization admin ({user?.org_name}).</p>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Settings & Admin</h1>
        <p className="text-sm text-muted-foreground">{user?.org_name} · integrations, users and system activity</p>
      </div>

      <Tabs defaultValue="integrations">
        <TabsList className="bg-white border">
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users & Roles</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">System Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="integrations" className="mt-4 space-y-4">
          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold"><MessageCircle className="h-4 w-4 text-green-600" /> WhatsApp Business API</h2>
              {wa && (
                <span data-testid="wa-status" className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${wa.mode === "simulated" ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" : "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"}`}>
                  {wa.mode === "simulated" ? "SIMULATION MODE" : "CREDENTIALS SAVED"}
                </span>
              )}
            </div>
            {wa && (
              <div className="space-y-4 p-5">
                <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  The platform is integration-ready: template dispatch, message logging, secure action links and the webhook receiver are live. Add your WhatsApp Business API credentials to activate real delivery — until then every message is simulated and recorded in the audit log.
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label>Provider</Label>
                    <Select value={wa.provider} onValueChange={(v) => setWa({ ...wa, provider: v })}>
                      <SelectTrigger data-testid="wa-provider" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="meta_cloud">Meta Cloud API</SelectItem>
                        <SelectItem value="twilio">Twilio</SelectItem>
                        <SelectItem value="gupshup">Gupshup</SelectItem>
                        <SelectItem value="360dialog">360dialog</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Phone Number ID</Label><Input data-testid="wa-phone-id" className="mt-1.5 font-mono" value={wa.phone_number_id} onChange={(e) => setWa({ ...wa, phone_number_id: e.target.value })} placeholder="10XXXXXXXXXXXXXXX" /></div>
                  <div><Label>Business Account ID</Label><Input data-testid="wa-business-id" className="mt-1.5 font-mono" value={wa.business_account_id} onChange={(e) => setWa({ ...wa, business_account_id: e.target.value })} /></div>
                  <div><Label>API Key / Permanent Token</Label><Input data-testid="wa-api-key" type="password" className="mt-1.5 font-mono" value={wa.api_key} onChange={(e) => setWa({ ...wa, api_key: e.target.value })} placeholder="Paste provider API key" /></div>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><Webhook className="h-3.5 w-3.5" /> Webhook URL (configure this in your provider console)</Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Input readOnly data-testid="wa-webhook-url" className="bg-slate-50 font-mono text-xs" value={wa.webhook_url} />
                    <Button variant="outline" data-testid="wa-copy-webhook" onClick={() => { navigator.clipboard.writeText(wa.webhook_url); toast.success("Webhook URL copied"); }}>Copy</Button>
                  </div>
                </div>
                <Button data-testid="wa-save-btn" onClick={saveWa} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Configuration
                </Button>
              </div>
            )}
          </div>

          <div className="rounded-lg border bg-card">
            <h2 className="flex items-center gap-2 border-b px-5 py-3 font-heading text-base font-semibold"><Mail className="h-4 w-4 text-blue-600" /> Email</h2>
            <div className="p-5">
              <span className="rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2.5 py-0.5 text-xs font-semibold text-[#92400E]">MOCK MODE</span>
              <p className="mt-3 text-sm text-muted-foreground">All emails are logged to the message log and PO communication history without external delivery. Plug in Resend / SendGrid / SES credentials here when you're ready to send real email.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h2 className="font-heading text-base font-semibold">Users & Roles</h2>
              <Button size="sm" data-testid="add-user-btn" onClick={() => setAddOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus className="mr-1.5 h-4 w-4" /> Add User</Button>
            </div>
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-2.5 font-medium">Name</th>
                  <th className="px-5 py-2.5 font-medium">Email</th>
                  <th className="px-5 py-2.5 font-medium">Role</th>
                  <th className="px-5 py-2.5 font-medium">Linked Supplier</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium">{u.name}</td>
                    <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-5 py-3"><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold capitalize">{u.role.replace("_", " ")}</span></td>
                    <td className="px-5 py-3 text-muted-foreground">{suppliers.find((s) => s.id === u.supplier_id)?.name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-4 space-y-4">
          {overview && (
            <>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                {Object.entries(overview.counts).map(([k, v]) => (
                  <div key={k} className="rounded-lg border bg-card p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{k.replace("_", " ")}</p>
                    <p className="mt-1 font-heading text-2xl font-semibold">{v}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border bg-card">
                <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Recent System Activity (Audit Log)</h2>
                <div className="divide-y" data-testid="activity-log">
                  {overview.recent_activity.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                      <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      <span className="flex-1">{e.message}</span>
                      <span className="text-xs text-muted-foreground">{e.actor_name}</span>
                      <span className="font-mono text-xs text-slate-400">{fmtDateTime(e.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">Add User</DialogTitle></DialogHeader>
          <form onSubmit={addUser} className="space-y-4">
            <div><Label>Name *</Label><Input data-testid="user-form-name" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email *</Label><Input data-testid="user-form-email" type="email" required className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Password *</Label><Input data-testid="user-form-password" required className="mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="procurement_manager">Procurement Manager</SelectItem>
                  <SelectItem value="org_admin">Organization Admin</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "supplier" && (
              <div>
                <Label>Linked Supplier *</Label>
                <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger data-testid="user-form-supplier" className="mt-1.5"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="user-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
